import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import FaceAuth from './components/FaceAuth';
import VoiceControl from './components/VoiceControl';
import DiagnosticTool from './components/DiagnosticTool';
import { openApp, sendMessage } from './services/AppService';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [appStarted, setAppStarted] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoggedOut, setUserLoggedOut] = useState(false);

  // Initialize app on mount
  useEffect(() => {
    // Add a welcome notification
    addNotification('Welcome to Voice Assistant! Begin by registering your face.');
    setAppStarted(true);
    
    // Check if we need to initialize auth state
    const savedAuth = localStorage.getItem('isAuthenticated');
    if (savedAuth === 'true') {
      const savedUsername = localStorage.getItem('faceUsername');
      setIsAuthenticated(true);
      setCurrentUser(savedUsername || 'User');
    } else {
      setIsAuthenticated(false);
      setCurrentUser(null);
    }
  }, []);

  // Handler for face authentication
  const handleAuthentication = (success, username = null) => {
    setIsAuthenticated(success);
    setCurrentUser(success ? username : null);
    
    // Store authentication state in localStorage
    try {
      localStorage.setItem('isAuthenticated', success ? 'true' : 'false');
    } catch (err) {
      console.error('Error storing authentication state:', err);
    }
    
    addNotification(success 
      ? `Authentication successful for ${username}. Voice commands enabled.` 
      : 'Authentication failed. Please try again.');
  };

  // Handler for opening apps
  const handleAppOpen = (appName) => {
    const result = openApp(appName);
    addNotification(result.message);
  };

  // Handler for sending messages
  const handleMessageSend = (contact, message) => {
    const result = sendMessage(contact, message);
    addNotification(result.message);
  };

  // Handler for logout
  const handleLogout = () => {
    // Clear authentication state
    setIsAuthenticated(false);
    setCurrentUser(null);
    setUserLoggedOut(true);
    
    // Store authentication state in localStorage
    try {
      localStorage.setItem('isAuthenticated', 'false');
      // Don't remove face descriptor here, just authentication state
    } catch (err) {
      console.error('Error clearing stored authentication:', err);
    }
    
    addNotification('Logged out successfully. Please authenticate again to use voice commands.');
    
    // Reset the logout flag after a short delay
    setTimeout(() => {
      setUserLoggedOut(false);
    }, 500);
  };

  // Add a notification message
  const addNotification = (message) => {
    const newNotification = {
      id: Date.now(),
      message,
      timestamp: new Date().toLocaleTimeString()
    };
    
    setNotifications(prevNotifications => [
      newNotification,
      ...prevNotifications.slice(0, 9) // Keep only the last 10 notifications
    ]);
  };

  // Clear all notifications
  const clearNotifications = () => {
    setNotifications([]);
    addNotification('Notifications cleared');
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Voice Assistant</h1>
        <p>Voice-controlled desktop assistant with face authentication</p>
        {currentUser && (
          <div className="current-user-header">
            Current user: <strong>{currentUser}</strong>
          </div>
        )}
      </header>

      <main className="App-main">
        <div className="authentication-section">
          <FaceAuth 
            onAuthenticated={handleAuthentication} 
            isLoggedOut={userLoggedOut} 
          />
          {isAuthenticated && (
            <button 
              className="logout-button" 
              onClick={handleLogout}
            >
              Logout
            </button>
          )}
        </div>

        <div className="voice-section">
          <VoiceControl 
            isAuthenticated={isAuthenticated} 
            onAppOpen={handleAppOpen}
            onMessageSend={handleMessageSend}
            onLogout={handleLogout}
          />
        </div>

        <div className="notification-section">
          <div className="notification-header">
            <h2>Activity Log</h2>
            {notifications.length > 1 && (
              <button 
                className="clear-notifications" 
                onClick={clearNotifications}
              >
                Clear
              </button>
            )}
          </div>
          <div className="notification-list">
            {notifications.length === 0 ? (
              <p className="no-notifications">No activity yet</p>
            ) : (
              notifications.map(notification => (
                <div key={notification.id} className="notification-item">
                  <span className="notification-time">{notification.timestamp}</span>
                  <span className="notification-message">{notification.message}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      <footer className="App-footer">
        <p>
          Use voice commands like "Open social media", "Search for something", "Play video on YouTube", or "Log out"
        </p>
        <p className="small">
          Note: This app works best in Chrome. Speech recognition and camera access required.
        </p>
      </footer>

      <DiagnosticTool />
    </div>
  );
}

export default App;
